<template>
    <app-layout :enable-flash-message="false" class="no-main-padding">
        <page-header :title="trans('workorder.overview.headline')" title-in-app-header>
            <div class="mt-3 flex flex-wrap items-start gap-3">
                <filter-bar v-model="queryParams" :config="filterBarConfig" class="min-w-0 flex-1">
                    <template #locations-option="{ option }">
                        <div class="flex items-center justify-between gap-3">
                            <span>{{ option.label }}</span>
                            <small class="text-muted">{{ option.iataCode }}</small>
                        </div>
                    </template>
                    <template #types-option="{ option }">
                        <div class="flex items-center justify-between">
                            <span class="mr-3">{{ option.label }}</span>
                            <small class="text-muted">{{ option.shortLabel }}</small>
                        </div>
                    </template>
                    <template #operabilities-option="{ option }">
                        <div class="flex items-center justify-between gap-1">
                            <span class="mr-3">{{ option.label }}</span>
                            <em :class="option.value" class="operability-indicator" />
                        </div>
                    </template>
                    <template #priorities-option="{ option }">
                        <div class="flex items-center justify-between gap-2">
                            <span>{{ option.label }}</span>
                            <high v-if="option.value === 'high'" />
                            <medium v-if="option.value === 'medium'" />
                            <low v-if="option.value === 'low'" />
                        </div>
                    </template>
                </filter-bar>
                <div class="shrink-0">
                    <modal-link
                        v-if="hasAnyPermission('workorder.create')"
                        :disabled="isOperationInProgress"
                        :href="route('workorders.create', { show_search: true })"
                        variant="secondary"
                    >
                        {{ trans('workorder.overview.action.create') }}
                    </modal-link>
                </div>
            </div>
        </page-header>
        <div>
            <h3 class="h5 py-2">
                {{ trans('workorder.overview.board.title') }}
            </h3>
            <work-order-board :selected-work-order="selectedWorkOrder" :swimlanes="swimlanes" class="pb-3" />

            <h3 class="h5 flex items-center gap-1 py-2">
                {{ trans('workorder.overview.records.title') }}
                <info-icon :tooltip="trans('assets.workorders.records.info')" style="position: relative; top: -1px" />
            </h3>

            <work-order-records />
            <suggest-asset-status-change-prompt-modal />
        </div>
    </app-layout>
</template>
<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import WorkOrderRecords from '@/Pages/WorkOrders/WorkOrderRecords.vue';
import WorkOrderBoard from '@/Pages/WorkOrders/WorkOrderBoard.vue';
import FilterBar from '@/Components/FilterBar.vue';
import InfoIcon from '@/Components/InfoIcon.vue';
import SuggestAssetStatusChangePromptModal from '@/Pages/Assets/Details/SuggestAssetStatusChangePromptModal.vue';
import PageHeader from '@/Components/PageHeader.vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n';
import High from '@/../icons/High.vue';
import Medium from '@/../icons/Medium.vue';
import Low from '@/../icons/Low.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import { useWorkOrderStore } from '@/stores/workOrders';

const page = usePage();
const workOrderStore = useWorkOrderStore();
const queryParams = ref(null);

const locations = computed(() =>
    (page.props.filters.locations?.data || []).map((loc) => ({
        label: loc.name,
        value: loc.icaoCode,
        iataCode: loc.iataCode,
        searchableContent: `${loc.iataCode}|${loc.icaoCode}|${loc.name}`,
    })),
);
const specificLocations = computed(() => (page.props.filters.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.name })));
const types = computed(() => page.props.filters.types?.data || []);
const usages = computed(() => page.props.filters.usages?.data || []);
const operabilities = computed(() => page.props.filters.operabilities?.data || []);
const workOrderTypes = computed(() => page.props.filters.workOrderTypes?.data || []);
const priorities = computed(() => page.props.filters.priorities?.data || []);
const priorityEnabled = hasFeature('work_order_priority');
const assignees = computed(
    () =>
        page.props.filters.assignees?.map((assignee) => ({
            group: assignee.group,
            data: assignee.data.map((data) => ({
                label: data.name,
                value: data.id,
            })),
        })) || [],
);

const filterBarConfig = computed(() => ({
    filters: [
        { type: 'search', key: 'search', placeholder: trans('workorder.overview.filter.search') },
        { type: 'multi-select', key: 'locations', label: trans('dashboard.filter.locations'), options: locations.value, showSearchbar: true },
        {
            type: 'multi-select',
            key: 'specificLocations',
            label: trans('activities.overview.cell.specific_location'),
            options: specificLocations.value,
            showSearchbar: true,
        },
        { type: 'multi-select', key: 'types', label: trans('workorder.overview.filter.type'), options: types.value },
        { type: 'multi-select', key: 'usages', label: trans('workorder.overview.filter.usage'), options: usages.value },
        { type: 'multi-select', key: 'operabilities', label: trans('workorder.overview.filter.operability'), options: operabilities.value },
        { type: 'multi-select', key: 'workorders', label: trans('workorder.overview.filter.workorder'), options: workOrderTypes.value },
        ...(priorityEnabled
            ? [{ type: 'multi-select', key: 'priorities', label: trans('workorder.overview.filter.priority'), options: priorities.value }]
            : []),
        {
            type: 'multi-select',
            key: 'assignees',
            label: trans('workorder.overview.filter.assignee'),
            options: assignees.value,
            showSearchbar: true,
            styles: { width: '325px' },
        },
    ],
}));

const selectedWorkOrder = ref(null);
const isOperationInProgress = ref(false);
const swimlanes = computed(() => page.props.swimlanes || []);

watch(
    () => queryParams.value?.form,
    () => {
        workOrderStore.reloadLanes();
        workOrderStore.reloadArchived();
    },
    { deep: true },
);

onMounted(() => {
    selectedWorkOrder.value = page.props.selectedWorkOrder;

    workOrderStore.setup();
    workOrderStore.setQueryParamsSourceRef(queryParams);
    workOrderStore.reloadLanes();
});

onBeforeUnmount(() => {
    workOrderStore.dispose();
});
</script>
